"use client";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { BannerBlock } from "../index";
import { parseHtml, toWebp } from "@/utils/common";
import Button from "@/Components/Button";
import style from "./style.module.scss";
import dynamic from "next/dynamic";
import { useAutoplayUnlock } from "@/hooks/useAutoplayUnlock";

const BlogCards = dynamic(() => import("@/Components/DataDisplayCompoants/BlogCard"), { ssr: true });
const BlueCards = dynamic(() => import("@/Components/DataDisplayCompoants/BlueCard"), { ssr: true });
const GreenCards = dynamic(() => import("@/Components/DataDisplayCompoants/GreenCard"), { ssr: true });

interface BannerProps {
  data?: BannerBlock;
  isMobile?: boolean;
}

type CardVariant = "blog" | "green" | "blue";

interface CardLink {
  label?: any;
  href?: any;
}

interface Card {
  varient: CardVariant;
  title: string;
  description: string;
  link?: CardLink;
  image?: any;
}

const Banner: React.FC<BannerProps> = ({ data }) => {
  const [currentIndex, setCurrentIndex] = useState(1);
  const bannerTitle = data?.title;
  const bannerSubTitle = data?.sub_title;
  const bannerDescription = data?.description;
  const cta = data?.Cta?.label;
  const href = data?.Cta?.href;
  const bannerAnimation = data?.banner_animation?.data?.attributes?.url;
  const bannerAnimationLink = data?.banner_animation_link;

  const paginationRef = useRef<HTMLDivElement | null>(null);
  const [paginationEl, setPaginationEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (paginationRef.current && !paginationEl) {
      setPaginationEl(paginationRef.current);
    }
  }, [paginationEl]);

  const bannerCards = (data?.bannerCard || []) as Card[];
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

  const isVideoFile = (filename: string): boolean => {
    const ext = filename.toLowerCase().substring(filename.lastIndexOf("."));
    return [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"].includes(ext);
  };

  const isImageFile = (filename: string): boolean => {
    const ext = filename.toLowerCase().substring(filename.lastIndexOf("."));
    return [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp"].includes(
      ext,
    );
  };

  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) video.play().catch(() => { });
          else video.pause();
        });
      },
      { threshold: 0.25 },
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  useAutoplayUnlock(videoRef);

  const renderCard = ({
    card,
    widthClass,
    index,
  }: {
    card: Card;
    widthClass: string;
    index: number;
  }) => {
    switch (card.varient) {
      case "blog":
        return (
          <BlogCards
            key={index}
            classname={`${widthClass} lg:h-auto h-full md:min-h-[300px] min-h-[320px]`}
            title={card?.title}
            details={card?.description}
            label={card?.link?.label}
            link={card?.link?.href}
            imageUrl={`${baseUrl}${card?.image?.data?.attributes?.url}`}
          />
        );
      case "green":
        return (
          <GreenCards
            key={index}
            classname={`${widthClass} lg:h-auto h-full md:min-h-[300px] min-h-[320px]`}
            title={card?.title}
            details={card?.description}
            label={card?.link?.label}
            link={card?.link?.href}
          />
        );
      case "blue":
      default:
        return (
          <BlueCards
            key={index}
            classname={`${widthClass} lg:h-auto h-full md:min-h-[300px] min-h-[320px]`}
            title={card?.title}
            details={card?.description}
            label={card?.link?.label}
            link={card?.link?.href}
          />
        );
    }
  };

  return (
    <>
      <section className="relative bg-gradient-before overflow-hidden lg:pt-[86px] pt-[80px]">
        <div className="relative z-10">
          {/* --- Banner Background --- */}
          {bannerAnimationLink ? (
            // CDN/external link
            <video
              ref={videoRef}
              src={bannerAnimationLink}
              width={1353}
              height={761}
              autoPlay
              muted
              loop
              playsInline
              disablePictureInPicture
              disableRemotePlayback
              preload="metadata"
              poster="/images/poster/banner-poster.webp"
              className="md:w-[60%] w-full object-cover md:h-[761px] h-[50%] max-h-full absolute top-0 md:right-0 -right-1/2 z-0 opacity-5"
              aria-label="Banner background video (CDN)"
            />
          ) : bannerAnimation && isVideoFile(bannerAnimation) ? (
            <video
              ref={videoRef}
              src={`${baseUrl}${bannerAnimation}`}
              width={1353}
              height={761}
              autoPlay
              muted
              loop
              playsInline
              disablePictureInPicture
              disableRemotePlayback
              preload="metadata"
              poster="/images/poster/banner-poster.webp"
              className="md:w-[60%] w-full object-cover md:h-[761px] h-[50%] max-h-full absolute top-0 md:right-0 -right-1/2 z-0 opacity-5"
              aria-label="Banner background video"
            />
          ) : bannerAnimation && isImageFile(bannerAnimation) ? (
            <Image
              src={toWebp(`${baseUrl}${bannerAnimation}`)}
              unoptimized
              width={1353}
              height={761}
              alt="Banner background image"
              className="w-[60%] object-cover h-[761px] max-h-full absolute -top-[60px] right-0 z-0 opacity-5"
              loading="eager"
              priority={true}
              fetchPriority={"high"}
            />
          ) : null}

          <Image
            src="/images/banner-top-vector.webp"
            width={1200}
            height={1200}
            alt="banner-vector-top"
            className="absolute w-[70%] max-w-[1200px] h-auto top-[-80px] right-0 z-0"
            style={{ aspectRatio: '1/1' }}
            loading="eager"
            fetchPriority={"high"}
            priority={true}
          />
          <Image
            src="/images/banner-vector.webp"
            width={900}
            height={900}
            alt="banner-vector"
            className="absolute w-[70%] max-w-[900px] h-auto bottom-[50%] translate-y-1/2 left-[-31px] opacity-5 z-0"
            style={{ aspectRatio: '1/1' }}
            loading="eager"
            fetchPriority={"high"}
            priority={true}
          />

          {/* --- Banner Text --- */}
          <div className="container relative z-10">
            <div className="lg:pt-[121px] md:pt-[82px] pt-[41px] lg:pb-[120px] md:pb-[80px] pb-[64px]">
              <p className="text-subtitle-1 font-bold text-primary-text-white">
                {bannerSubTitle}
              </p>
              <h1
                className="text-display font-extrabold text-primary-text-white uppercase mt-4 max-w-[1078px]"
                style={{
                  willChange: 'auto',
                  contain: 'layout style paint'
                }}
              >
                {parseHtml(bannerTitle)}
              </h1>
              <div className="flex justify-start items-stretch lg:gap-6 gap-4 mt-4">
                <div className="w-1.5 bg-[linear-gradient(179.61deg,_#8ABC00_3.58%,_#0086E6_97.78%)]"></div>
                <div className="xl:py-[5.5px] lg:py-[13.5px]">
                  <p className="text-secondary-text-white text-subtitle-2 font-regular xl:max-w-[580px] max-w-[587px]">
                    {bannerDescription}
                  </p>
                  {href && cta && (
                    <Link className="mt-4 flex" href={href}>
                      <Button label={cta} />
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* --- Desktop / Tablet Layout --- */}
            <div className="hidden md:block w-full">
              <>
                {/* iPad Portrait */}
                <div
                  className={`grid grid-cols-3 gap-[30px] ${style["hiddle-ipad-landscape"]}`}
                >
                  {[0, 1, 2].map((col) => (
                    <div className="flex flex-col gap-[30px]" key={col}>
                      {bannerCards
                        .slice(col * 2, col * 2 + 2)
                        .map((card, index) => {
                          const widthClass =
                            "w-full max-h-[300px] aspect-[301/300]";
                          return renderCard({ card, widthClass, index });
                        })}
                    </div>
                  ))}
                </div>

                {/* Desktop */}
                <div className={`${style["hiddle-ipad-portrait"]}`}>
                  <div className="flex lg:flex-nowrap flex-wrap gap-[30px]">
                    {bannerCards.slice(0, 3).map((card, index) => {
                      const widthClass =
                        index === 0
                          ? "xl:w-[40%] w-[35%] xl:max-h-[320px] max-h-[300px] xl:aspect-[495/320] aspect-[301/300]"
                          : index === 1
                            ? "xl:w-[32%] w-[30%] xl:max-h-[320px] max-h-[300px]"
                            : "xl:w-[23%] w-[28%] xl:max-h-[320px] max-h-[300px]";
                      return renderCard({ card, widthClass, index });
                    })}
                  </div>
                  <div className="flex lg:flex-nowrap flex-wrap gap-[30px] mt-[30px]">
                    {bannerCards.slice(3, 6).map((card, index) => {
                      const widthClass =
                        index === 2
                          ? "xl:w-[40%] w-[35%] xl:max-h-[320px] max-h-[300px] xl:aspect-[495/320] aspect-[301/300]"
                          : index === 1
                            ? "xl:w-[32%] w-[30%] xl:max-h-[320px] max-h-[300px]"
                            : "xl:w-[23%] w-[28%] xl:max-h-[320px] max-h-[300px]";
                      return renderCard({ card, widthClass, index });
                    })}
                  </div>
                </div>
              </>
            </div>
          </div>

          {/* --- Mobile Swiper --- */}
          <div className="block md:hidden w-full">
            <div className="block">
              <Swiper
                modules={[Autoplay, Pagination]}
                spaceBetween={15}
                centeredSlides
                // `rewind` rather than `loop`: loop mode reorders the slide
                // elements in the DOM on every cycle (loopFix), which is a real
                // layout change and registered a CLS hit every few seconds for
                // as long as the page stayed open. Rewind cycles by animating
                // back to the first slide instead, leaving the DOM alone.
                rewind
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }}
                onSlideChange={(swiper) =>
                  setCurrentIndex(swiper.realIndex + 1)
                }
                breakpoints={{
                  0: { slidesPerView: 1.4 },
                  500: { slidesPerView: 1.5 },
                  1024: { slidesPerView: 1.1 },
                }}
                className="w-full"
              >
                {bannerCards.map((card, index) => (
                  <SwiperSlide key={index}>
                    <div className="w-[71vw] max-w-[306px] h-[75vw] min-h-[320px] mx-auto">
                      {renderCard({ card, widthClass: "w-full h-full", index })}
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Mobile Progress Bar */}
              <div className="mt-[30px] lg:mt-[15px] flex justify-center items-center gap-2 px-12 py-6 sm:w-[67%] mx-auto relative sm:absolute bottom-[15%] lg:bottom-[20%] left-[50%] sm:transform -translate-x-[50%] z-[5]">
                {bannerCards.map((_, idx) => (
                  <div
                    key={idx}
                    className={`p-0 relative rounded overflow-hidden ${currentIndex - 1 === idx
                      ? " flex w-[128px] h-1 bg-[#E6F3FD]"
                      : "flex-none w-[16px] h-1 bg-[#E6F3FD]"
                      }`}
                  >
                    <span
                      className={`absolute top-0 rounded left-0  h-1 ${currentIndex - 1 === idx
                        ? "bg-gradient-to-r from-[#8ABC00] to-[#0086E6] transition-all duration-[3000ms] w-[128px] "
                        : "left-[-100%] w-[16px] "
                        }`}
                    ></span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// Memoize Banner to prevent unnecessary re-renders and reduce TBT
export default React.memo(Banner);
